// System roles — source of truth: task-to-implement/system-roles.md + system-roles-answers.md.
//
// The backend seeds three frozen roles (`isSystem: true`, undeletable/immutable via the API).
// Two of them — `configuration_manager` and `access_manager` — carry NO operations; they exist
// only as markers the console keys sidebar-group visibility off. `super_admin` is the third
// system role and holds every operation.
//
// Wire-name note (answers §1): the role JSON really uses `isSuperAdmin` / `isSystem` — the
// generated OpenAPI schema drops the `is` prefix but Jackson keeps it. Trust the JSON, not the schema.

export const SYSTEM_ROLE_CODE = {
  superAdmin: 'super_admin',
  configurationManager: 'configuration_manager',
  accessManager: 'access_manager',
} as const

export type SystemRoleCode = (typeof SYSTEM_ROLE_CODE)[keyof typeof SYSTEM_ROLE_CODE]

// The two marker roles that grant nothing but reveal a sidebar group (answers §2).
export const MARKER_ROLE_CODES = [
  SYSTEM_ROLE_CODE.configurationManager,
  SYSTEM_ROLE_CODE.accessManager,
] as const

const SYSTEM_ROLE_CODES: readonly string[] = Object.values(SYSTEM_ROLE_CODE)

export const isSystemRoleCode = (code: string): boolean => SYSTEM_ROLE_CODES.includes(code)

// A marker holds zero operations — assigning it alone leaves a user unable to open any page.
export const isMarkerRoleCode = (code: string): boolean =>
  (MARKER_ROLE_CODES as readonly string[]).includes(code)

// System-role names are seeded in English and frozen on the backend, so we localize the three
// known codes on the console (answers §6). Custom roles keep whatever name the admin typed.
export const roleDisplayName = (
  role: { code: string; name: string },
  t: (key: string) => string,
): string => (isSystemRoleCode(role.code) ? t(`admin.roles.systemNames.${role.code}`) : role.name)

// For "who am I" surfaces (sidebar account card, profile) the marker roles are noise — they grant
// nothing and read as clutter next to a real role. Drop them, but never show an empty label: a
// user holding ONLY markers falls back to showing those markers.
export const displayableRoles = <T extends { code: string }>(roles: readonly T[]): T[] => {
  const meaningful = roles.filter((role) => !isMarkerRoleCode(role.code))
  return meaningful.length ? meaningful : [...roles]
}
